<template>
    <v-dialog
        v-model="show_dialog"
        max-width="800"
        @input="onDialogVisibilityChange"
    >
        <v-card>
            <v-card-title class="headline">
                Create new automatic message
            </v-card-title>

            <v-card-text>
                <v-select
                    v-if="template_choices.length > 1"
                    label="Select a message type"
                    :items="template_choices"
                    v-model="selected_template_choice"
                    item-text="text"
                    item-value="value"
                    return-object
                >
                    <template v-slot:item="data">
                        <div
                            v-if="data.item"
                            class="d-flex align-center justify-space-between"
                        >
                            <span>{{ data.item.text }}</span>
                            <div
                                v-if="getRestrictions(data.item).length"
                                class="restriction-chip-group ml-4"
                            >
                                <v-chip
                                    v-for="restriction in getRestrictions(data.item)"
                                    :key="`${data.item.value}-${restriction.code}`"
                                    :color="getRestrictionChipColor(restriction)"
                                    :text-color="getRestrictionChipTextColor(restriction)"
                                    small
                                    pill
                                    :title="getRestrictionDescription(restriction)"
                                >
                                    {{ restriction.label }}
                                </v-chip>
                            </div>
                        </div>
                    </template>

                    <template v-slot:selection="data">
                        <span v-if="data.item">{{ data.item.text }}</span>
                    </template>
                </v-select>

                <div v-if="selected_template_choice && getRestrictions(selected_template_choice).length" class="mt-2 mb-5">
                    <div class="text-caption theme--light v-label mb-0">Message restrictions</div>

                    <div
                        class="restriction-list mt-2"
                    >
                        <ul class="restriction-bullets">
                            <li
                                v-for="restriction in getRestrictions(selected_template_choice)"
                                :key="`selected-${restriction.code}`"
                                class="restriction-bullet"
                            >
                                <span class="text--secondary">{{ getRestrictionDescription(restriction) }}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <m-edit-select-organization-owned-endpoint
                    v-if="selected_template_choice"
                    label="Send From"
                    :organization_id="organization_id"
                    v-model="template_send_from"
                />
            </v-card-text>

            <v-card-actions>
                <v-spacer />

                <v-btn
                    color="gray darken-1"
                    text
                    @click="onCancel"
                >
                    Cancel
                </v-btn>

                <v-btn
                    color="primary darken-1"
                    text
                    @click="onConfirm"
                    :disabled="!selected_template_choice || !isTemplateSendFromValid"
                >
                    Create
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    props: {
        organization_id: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            show_dialog: false,
            template_choices: [],
            selected_template_choice: null,
        };
    },

    methods: {
        showDialog(template_choices, preselected_template_choice = null) {
            this.template_choices = template_choices;
            this.selected_template_choice = preselected_template_choice;
            this.show_dialog = true;
        },

        onDialogVisibilityChange(isVisible) {
            if (!isVisible) {
                this.selected_template_choice = null;
            }
        },

        onCancel() {
            this.show_dialog = false;
            this.selected_template_choice = null;
        },

        onConfirm() {
            if (this.selected_template_choice) {
                this.show_dialog = false;
                this.$emit('confirm', this.selected_template_choice);
                this.selected_template_choice = null;
            }
        },

        getRestrictions(choice) {
            if (!choice) return [];

            if (Array.isArray(choice.restrictions) && choice.restrictions.length > 0) {
                return choice.restrictions;
            }

            if (choice.template?.template_restrictions?.length) {
                return choice.template.template_restrictions;
            }

            return [];
        },

        getRestrictionChipColor(restriction) {
            if (!restriction) return 'grey lighten-4';

            if (restriction.type === 'staff-only') {
                return 'red lighten-4';
            }

            if (restriction.type === 'integration') {
                return 'indigo lighten-5';
            }

            return 'grey lighten-4';
        },

        getRestrictionChipTextColor(restriction) {
            if (!restriction) return 'grey darken-2';

            if (restriction.type === 'staff-only') {
                return 'red darken-2';
            }

            if (restriction.type === 'integration') {
                return 'indigo darken-3';
            }

            return 'grey darken-2';
        },

        getRestrictionDescription(restriction) {
            if (!restriction) return '';
            return restriction.description || 'Available when requirements are met.';
        },
    },

    computed: {
        template_send_from: {
            get() {
                if (!this.selected_template_choice) return null;
                
                const template = this.selected_template_choice.template;
                
                if (template.conditions && template.conditions.length > 0) {
                    return template.conditions[0].send_from;
                }
                
                return template.send_from;
            },
            set(value) {
                if (!this.selected_template_choice) return;
                
                const template = this.selected_template_choice.template;
                template.send_from = value;
                
                if (template.conditions && template.conditions.length > 0) {
                    template.conditions.forEach(condition => {
                        condition.send_from = value;
                    });
                }
            }
        },

        isTemplateSendFromValid() {
            if (!this.selected_template_choice) return false;
            
            const template = this.selected_template_choice.template;
            
            if (template.conditions && template.conditions.length > 0) {
                return template.conditions.every(condition => !!condition.send_from);
            }
            
            return !!template.send_from;
        },
    },
};
</script>

<style lang="scss" scoped>
.restriction-chip-group {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.restriction-chip-group .v-chip {
    margin: 0;
}

.restriction-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.restriction-list {
    margin-top: 8px;
}

.restriction-bullets {
    margin: 0;
    padding-left: 18px;
}

.restriction-bullet {
    margin-bottom: 6px;
}
</style>
